/**
 * AiIcdSuggester — small inline AI helper that reads the clinician's free-text
 * diagnosis/SOAP note and proposes 3 most likely ICD-10 codes. Iter 73.
 *
 * Usage:
 *   <AiIcdSuggester text={diagnosis} patientId={pid}
 *                   onPick={(code) => addIcdCode(code)} />
 */
import React, { useState } from 'react';
import { api } from '../lib/api';
import { Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AiIcdSuggester({ text, patientId, onPick, testidPrefix = 'ai-icd' }) {
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  const run = async () => {
    if (!text || !text.trim() || text.trim().length < 10) {
      toast.error('Add a bit more detail to your note (10+ characters) before asking for ICD codes.');
      return;
    }
    setBusy(true); setErr(''); setSuggestions([]);
    try {
      const { data } = await api.post('/ai/icd/suggest', {
        text: text.trim(),
        patient_id: patientId || undefined,
      });
      setSuggestions(data.suggestions || []);
      setOpen(true);
      if (!data.suggestions?.length) {
        toast.info('No matching ICD-10 codes found — refine your note and try again.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'AI ICD suggester failed.';
      setErr(detail);
      toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <button type="button" onClick={run} disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-brand/30 text-brand hover:bg-brand/5 transition disabled:opacity-50"
              data-testid={`${testidPrefix}-run`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Sparkles className="w-3.5 h-3.5"/>}
        {busy ? 'Asking AI…' : 'Suggest ICD-10 codes'}
      </button>

      {err && (
        <div className="text-xs text-[#B71C1C] flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5"/> {err}
        </div>
      )}

      {open && suggestions.length > 0 && (
        <div className="border border-soft rounded-lg p-3 bg-bg-secondary/40 space-y-1.5" data-testid={`${testidPrefix}-results`}>
          <div className="text-[10px] uppercase tracking-wider text-fg-muted font-semibold mb-1">
            AI suggested codes — click to add
          </div>
          {suggestions.map((s) => (
            <button key={s.code} type="button" onClick={() => { onPick?.(s); toast.success(`Added ${s.code}`); }}
                    className="w-full text-left flex items-start gap-2 p-2 rounded hover:bg-bg-primary border border-transparent hover:border-soft transition"
                    data-testid={`${testidPrefix}-pick-${s.code}`}>
              <CheckCircle2 className="w-4 h-4 text-brand mt-0.5 flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold text-brand">{s.code}</span>
                  <span className="text-sm">{s.name}</span>
                  <ConfidencePill level={s.confidence}/>
                </div>
                {s.rationale && <div className="text-[11px] text-fg-muted mt-0.5">{s.rationale}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfidencePill({ level }) {
  const m = {
    high: 'bg-[#1B5E20]/10 text-[#1B5E20]',
    med:  'bg-[#FFB000]/15 text-[#A86F31]',
    low:  'bg-fg-muted/10 text-fg-muted',
  };
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${m[level] || m.med}`}>{level || 'med'}</span>;
}
